// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { SupportSnackbar } from '@/components/support-snackbar';
import { FormProjectSearch } from '@/project/components/form-project-search';
import { projectIdentifierSchema } from '@/sparse/api/types';
import { Ticket } from '@/ticket/api/types';
import { useUpdateTicket } from '@/ticket/hooks/use-update-ticket';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({ projects: z.array(projectIdentifierSchema) });

type FormData = z.infer<typeof formSchema>;

interface Props {
  isOpen: boolean;
  ticket: Ticket;
  onClose: () => void;
}

export function TicketProjectsDialog(props: Props) {
  const { control, formState, reset, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      projects: props.ticket.projects,
    },
  });

  const queryClient = useQueryClient();
  const updateTicketMutation = useUpdateTicket(props.ticket.key, {
    onSuccess: (data) => {
      queryClient.setQueryData(['tickets', props.ticket.key], data);
      reset({
        projects: data.projects,
      });
      props.onClose();
    },
  });

  const shouldSave = formState.isDirty;

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (shouldSave) {
      return updateTicketMutation.mutate({
        projects: data.projects.map((record) => record.id),
      });
    }

    props.onClose();
  };

  return (
    <>
      <SupportSnackbar
        isOpen={updateTicketMutation.isError}
        message="An error occurred while updating the project."
        onClose={updateTicketMutation.reset}
      />
      <Dialog
        fullWidth
        maxWidth="md"
        open={props.isOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') {
            return;
          }
          props.onClose();
        }}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(handleValidSubmission),
        }}
      >
        <DialogTitle>Attach projects</DialogTitle>
        <DialogContent dividers>
          <FormProjectSearch
            multiple
            name="projects"
            label="Projects"
            control={control}
            disabled={updateTicketMutation.isPending}
          />
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            onClick={() => {
              reset({
                projects: props.ticket.projects,
              });
              props.onClose();
            }}
            disabled={updateTicketMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            size="small"
            type="submit"
            color="secondary"
            variant="contained"
            disabled={updateTicketMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
